import { input, query } from "@/composables/store";

export type CompanySearchHistoryPayload = {
	value?: unknown;
	filter?: unknown;
	competitor_lookalike?: unknown;
};

export type CompanySearchHistoryEntry = {
	id: string;
	payload: CompanySearchHistoryPayload;
	useCount: number;
	searchedAt: string;
};

export type CompanyHistoryApplyRequest = {
	sequence: number;
	payload: CompanySearchHistoryPayload;
	requestedAt: number;
};

export type CompanyHistoryRouteQuery = {
	query?: string;
	value: string;
	competitor_lookalike?: string;
};

type CompanySearchHistoryRecord = {
	record_id: string;
	payload: CompanySearchHistoryPayload | null;
	use_count?: number | null;
	searched_date: string;
};

type CompanyFilterValue = {
	value?: unknown;
	values?: unknown;
	min?: unknown;
	max?: unknown;
};

export const COMPANY_HISTORY_APPLY_STATE_KEY = "company-history-apply-request";

const FILTER_LABELS: Record<string, string> = {
	classification: "Classification",
	country: "Country",
	employees: "Number of Employees",
	eight_digit_sic_code: "8-Digit SIC Code",
	facebook: "Facebook URL",
	four_digit_sic_code: "4-Digit SIC Code",
	home_about_contact: "Website Text",
	industry: "Industry",
	keywords: "Keywords",
	linkedin: "LinkedIn URL",
	location: "State",
	name: "Company Name",
	naics_code: "NAICS Code",
	near_zip_code: "ZIP Code",
	offers_financing: "Offers Financing",
	phone: "Phone",
	prediction_probability: "Prediction Probability",
	radius: "Radius",
	region: "Region",
	revenue: "Annual Revenue",
	time_in_business: "Time in Business",
	twitter: "Twitter URL",
	two_digit_sic_code: "2-Digit SIC Code",
};

function toCompanySearchHistoryEntry(record: CompanySearchHistoryRecord): CompanySearchHistoryEntry {
	return {
		id: record.record_id,
		payload: record.payload && typeof record.payload === "object" ? record.payload : {},
		useCount: Number(record.use_count ?? 1) || 1,
		searchedAt: record.searched_date,
	};
}

function uniqueStrings(values: unknown[]) {
	const seen = new Set<string>();
	return values.flatMap((value) => {
		const trimmed = String(value ?? "").trim();
		if (!trimmed || seen.has(trimmed)) return [];
		seen.add(trimmed);
		return [trimmed];
	});
}

export function parseCompanyHistorySearchValues(value: unknown): string[] {
	if (Array.isArray(value)) return uniqueStrings(value);
	const raw = String(value ?? "").trim();
	if (!raw) return [];
	if (raw.startsWith("[") && raw.endsWith("]")) {
		try {
			const parsed = JSON.parse(raw);
			if (Array.isArray(parsed)) return uniqueStrings(parsed);
		} catch {
			// Fall through to the same separators accepted by the Companies input.
		}
	}
	return uniqueStrings(raw.split(/[;\n]+|\s+\bOR\b\s+/gi));
}

export function parseCompanyHistoryFilters(value: unknown): Record<string, CompanyFilterValue> {
	let parsed = value;
	if (typeof parsed === "string") {
		try {
			parsed = JSON.parse(parsed);
		} catch {
			return {};
		}
	}
	if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
	return parsed as Record<string, CompanyFilterValue>;
}

function isLookalikeSearch(value: unknown) {
	return value === true || value === "true" || value === "1" || value === 1;
}

function serializeSearchValues(values: string[]) {
	return values.length === 1 ? values[0] : JSON.stringify(values);
}

export function companyHistoryRouteQuery(payload: CompanySearchHistoryPayload): CompanyHistoryRouteQuery | null {
	const values = parseCompanyHistorySearchValues(payload.value);
	if (values.length === 0) return null;
	const filters = parseCompanyHistoryFilters(payload.filter);
	return {
		...(Object.keys(filters).length > 0 ? { query: JSON.stringify(filters) } : {}),
		value: serializeSearchValues(values),
		...(isLookalikeSearch(payload.competitor_lookalike) ? { competitor_lookalike: "true" } : {}),
	};
}

function formatNumericValue(key: string, value: number) {
	if (key === "revenue") {
		return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);
	}
	if (key === "time_in_business") return `${value.toLocaleString()} year${value === 1 ? "" : "s"}`;
	if (key === "radius") return `${value.toLocaleString()} mi`;
	return value.toLocaleString();
}

function finiteNumber(value: unknown): number | null {
	if (value === null || value === undefined || value === "") return null;
	const number = Number(value);
	return Number.isFinite(number) ? number : null;
}

function formatFilterValue(key: string, condition: CompanyFilterValue) {
	const values = Array.isArray(condition.values) ? uniqueStrings(condition.values) : [];
	if (values.length > 0) return values.join(", ");
	if (condition.value !== undefined && condition.value !== null) return String(condition.value).trim();

	const min = finiteNumber(condition.min);
	const max = finiteNumber(condition.max);
	if (min !== null && max !== null) return `${formatNumericValue(key, min)} - ${formatNumericValue(key, max)}`;
	if (min !== null) return `From ${formatNumericValue(key, min)}`;
	if (max !== null) return `Up to ${formatNumericValue(key, max)}`;
	return "";
}

export function summarizeCompanySearchPayloadParts(payload: CompanySearchHistoryPayload | null | undefined): string[] {
	if (!payload || typeof payload !== "object") return [];
	const searchValues = parseCompanyHistorySearchValues(payload.value);
	const searchLabel = isLookalikeSearch(payload.competitor_lookalike) ? "Website" : "Search";
	const searchParts = searchValues.length > 0 ? [`${searchLabel}: ${searchValues.join(" OR ")}`] : [];
	const filterParts = Object.entries(parseCompanyHistoryFilters(payload.filter)).flatMap(([key, condition]) => {
		if (!condition || typeof condition !== "object" || Array.isArray(condition)) return [];
		const formatted = formatFilterValue(key, condition);
		if (!formatted) return [];
		return [`${FILTER_LABELS[key] ?? key.replace(/_/g, " ")}: ${formatted}`];
	});
	return [...searchParts, ...filterParts];
}

export function summarizeCompanySearchPayload(payload: CompanySearchHistoryPayload | null | undefined, options: { maxParts?: number; maxPartLength?: number } = {}) {
	const { maxParts = 3, maxPartLength = 42 } = options;
	const parts = summarizeCompanySearchPayloadParts(payload).map((part) => (part.length > maxPartLength ? `${part.slice(0, maxPartLength)}…` : part));
	if (parts.length === 0) return "";
	const visible = parts.slice(0, maxParts);
	const hidden = parts.length - visible.length;
	return hidden > 0 ? `${visible.join(" · ")} +${hidden} more` : visible.join(" · ");
}

export function companySearchPayloadTooltip(title: string, payload: CompanySearchHistoryPayload | null | undefined) {
	return [title, ...summarizeCompanySearchPayloadParts(payload)].filter(Boolean).join("\n");
}

export async function fetchCompanySearchHistory(limit: number = 8): Promise<CompanySearchHistoryEntry[]> {
	const { list } = await query<CompanySearchHistoryRecord>("companies/search-history", undefined, { limit });
	return (list || []).map(toCompanySearchHistoryEntry);
}

export async function deleteCompanySearchHistoryEntry(id: string): Promise<void> {
	await input(`companies/search-history/${encodeURIComponent(id)}`, {}, {}, "DELETE");
}

export async function clearCompanySearchHistory(): Promise<void> {
	await input("companies/search-history", {}, {}, "DELETE");
}
